import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  CheckCircle,
  XCircle,
  Eye,
  Filter,
  Calendar,
  Clock,
  User,
  Layers,
  FileText,
  TrendingUp,
  Loader,
  ArrowUpDown,
  FileCheck2,
  ChevronRight,
  ChevronLeft
} from "lucide-react";
import authAxios from "../api/authAxios";
import Layout from "../components/Layout";
import { toast } from "react-hot-toast";
import EmptyState from "../components/EmptyState";

export default function OwnerPurchaseRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // "all", "pending", "approved", "rejected"
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/api/purchase/requests/");
      setRequests(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load purchase requests.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      await authAxios.post(`/api/purchase/requests/${id}/approve/`);
      toast.success("Purchase request approved successfully!");
      fetchRequests();
      if (showDetailModal) {
        setShowDetailModal(false);
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to approve purchase request.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id) => {
    setActionLoading(true);
    try {
      await authAxios.post(`/api/purchase/requests/${id}/reject/`);
      toast.success("Purchase request rejected.");
      fetchRequests();
      if (showDetailModal) {
        setShowDetailModal(false);
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to reject purchase request.");
    } finally {
      setActionLoading(false);
    }
  };

  // Filter requests
  const filteredRequests = requests.filter(req => {
    const matchesSearch =
      req.invoice_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (req.executive_name && req.executive_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (req.customer_name && req.customer_name.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesTab = activeTab === "all" || req.status === activeTab;

    return matchesSearch && matchesTab;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return (
          <span className="px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/10 flex items-center gap-1.5 w-fit">
            <CheckCircle className="w-3.5 h-3.5" />
            Approved
          </span>
        );
      case "rejected":
        return (
          <span className="px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider bg-red-500/20 text-red-400 border border-red-500/10 flex items-center gap-1.5 w-fit">
            <XCircle className="w-3.5 h-3.5" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/10 flex items-center gap-1.5 w-fit animate-pulse">
            <Layers className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: "3s" }} />
            Pending Approval
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-purple-50 to-slate-100 dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg p-4 md:p-8 transition-colors duration-250">
        
        {/* Header Section */}
        <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-purple-600/10 dark:bg-purple-600/20 text-purple-600 dark:text-purple-400 flex items-center justify-center shadow-md border border-purple-500/10">
              <FileCheck2 className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Purchase Requests
              </h1>
              <p className="text-sm text-gray-500 dark:text-slate-400 font-medium">
                Review and approve bulk stock transactions requested by your Sales Executives
              </p>
            </div>
          </div>
        </div>

        {/* Quick Insights Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 p-6 rounded-2xl shadow-sm hover:shadow-md transition">
            <p className="text-xs font-black text-purple-600 dark:text-purple-400 uppercase tracking-widest">Total Pending</p>
            <h3 className="text-3xl font-black text-gray-900 dark:text-white mt-1">
              {requests.filter(r => r.status === "pending").length} Requests
            </h3>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 p-6 rounded-2xl shadow-sm hover:shadow-md transition">
            <p className="text-xs font-black text-emerald-500 dark:text-emerald-400 uppercase tracking-widest">Approved Requests</p>
            <h3 className="text-3xl font-black text-gray-900 dark:text-white mt-1">
              {requests.filter(r => r.status === "approved").length} Requests
            </h3>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 p-6 rounded-2xl shadow-sm hover:shadow-md transition">
            <p className="text-xs font-black text-rose-500 dark:text-rose-400 uppercase tracking-widest">Total Valuation</p>
            <h3 className="text-3xl font-black text-gray-900 dark:text-white mt-1">
              ₹{requests.reduce((acc, curr) => acc + parseFloat(curr.total_amount), 0).toLocaleString()}
            </h3>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div className="bg-white dark:bg-slate-900 border border-gray-150 dark:border-slate-800 rounded-2xl p-4 mb-8 flex flex-col md:flex-row gap-4 items-center justify-between shadow-xs">
          {/* Status Tabs */}
          <div className="flex bg-slate-100 dark:bg-slate-950 p-1 rounded-xl w-full md:w-auto">
            {["all", "pending", "approved", "rejected"].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 md:flex-initial px-5 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition ${
                  activeTab === tab
                    ? "bg-purple-600 text-white shadow-md"
                    : "text-slate-500 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search by invoice, staff..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs font-medium focus:outline-none focus:border-purple-500 text-gray-800 dark:text-white placeholder-slate-400 transition"
            />
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-white dark:bg-slate-900 border border-gray-150 dark:border-slate-800 rounded-3xl overflow-hidden shadow-sm">
          {loading ? (
            <EmptyState
              preset="list"
              title="Fetching latest purchase requests..."
              message="Please wait a moment while we load your requests."
              className="py-12"
            />
          ) : filteredRequests.length === 0 ? (
            <EmptyState
              preset="list"
              title="No Purchase Requests found"
              message="Requests matching the filter criteria will display here."
              className="py-16"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse whitespace-nowrap">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-slate-800 text-xs font-black uppercase tracking-wider text-slate-400 bg-slate-50/50 dark:bg-slate-950/20">
                    <th className="px-6 py-4.5">Executive (Biller)</th>
                    <th className="px-6 py-4.5">Customer Details</th>
                    <th className="px-6 py-4.5">Invoice #</th>
                    <th className="px-6 py-4.5">Valuation</th>
                    <th className="px-6 py-4.5">Request Date</th>
                    <th className="px-6 py-4.5">Status</th>
                    <th className="px-6 py-4.5 text-right">Action Panel</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                  {filteredRequests.map(req => (
                    <tr
                      key={req.id}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-850/20 transition-colors"
                    >
                      {/* Biller / Executive */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-extrabold text-sm flex items-center justify-center">
                            {req.executive_name ? req.executive_name.slice(0, 2).toUpperCase() : "EX"}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white text-sm">
                              {req.executive_name || "Unknown"}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5">Sales Executive</p>
                          </div>
                        </div>
                      </td>

                      {/* Customer Name */}
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900 dark:text-white text-sm">
                          {req.customer_name || "Walk-In Customer"}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">Bulk Purchase Buyer</p>
                      </td>

                      {/* Invoice No */}
                      <td className="px-6 py-4 font-mono font-extrabold text-purple-600 dark:text-purple-400 text-sm">
                        {req.invoice_number}
                      </td>

                      {/* Valuation */}
                      <td className="px-6 py-4">
                        <p className="font-extrabold text-gray-900 dark:text-white text-sm">
                          ₹{parseFloat(req.total_amount).toFixed(2)}
                        </p>
                        <p className="text-[10px] font-black text-purple-500 mt-0.5 uppercase tracking-wider">GST Inc.</p>
                      </td>

                      {/* Date */}
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400 font-medium">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          {new Date(req.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric"
                          })}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-400">
                          <Clock className="w-3.5 h-3.5" />
                          {new Date(req.created_at).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        {getStatusBadge(req.status)}
                      </td>

                      {/* Action buttons */}
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* View Detail Trigger */}
                          <button
                            onClick={() => {
                              setSelectedRequest(req);
                              setShowDetailModal(true);
                            }}
                            className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg transition"
                            title="View Invoice Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {req.status === "pending" && (
                            <>
                              <button
                                onClick={() => handleApprove(req.id)}
                                disabled={actionLoading}
                                className="p-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-lg transition"
                                title="Approve Request"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReject(req.id)}
                                disabled={actionLoading}
                                className="p-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 rounded-lg transition"
                                title="Reject Request"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Invoice Detail Preview Side Panel/Modal */}
        <AnimatePresence>
          {showDetailModal && selectedRequest && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-4xl p-6 shadow-2xl relative overflow-hidden flex flex-col h-[90vh]"
              >
                {/* Header controls */}
                <div className="pb-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <h3 className="font-extrabold text-xl text-gray-900 dark:text-white">
                    Purchase Request: {selectedRequest.invoice_number}
                  </h3>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedRequest(null);
                    }}
                    className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-800"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                {/* Main scrollable invoice renderer matching EXACT pattern */}
                <div className="flex-1 overflow-y-auto py-6 space-y-6">
                  {/* Matching the SAME design pattern as InvoiceSuccessPage */}
                  <div className="border border-gray-200 dark:border-slate-800 rounded-2xl p-6 bg-slate-50/50 dark:bg-slate-950/25">
                    {/* Invoice header logo and meta */}
                    <div className="flex flex-col md:flex-row justify-between gap-6 mb-8">
                      <div>
                        <h2 className="text-3xl font-black text-purple-600 tracking-tight uppercase">
                          PURCHASE INVOICE
                        </h2>
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1">
                          Generated via Sales Executive Catalogue
                        </p>
                      </div>
                      <div className="text-left md:text-right font-medium text-slate-600 dark:text-slate-300">
                        <p className="text-sm font-extrabold text-gray-900 dark:text-white">
                          Invoice No: <span className="font-mono">{selectedRequest.invoice_number}</span>
                        </p>
                        <p className="text-xs mt-1">
                          Date: {new Date(selectedRequest.created_at).toLocaleString()}
                        </p>
                        <p className="text-xs mt-1">
                          Executive: {selectedRequest.executive_name}
                        </p>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-gray-200 dark:border-slate-800">
                      <div>
                        <p className="text-[10px] font-black uppercase text-purple-500 tracking-wider">
                          Buyer/Customer Details:
                        </p>
                        <h4 className="text-base font-extrabold text-gray-900 dark:text-white mt-1">
                          {selectedRequest.customer_name || "Walk-In Customer"}
                        </h4>
                        <p className="text-sm text-slate-400 mt-1">
                          {selectedRequest.invoice_data?.customer?.phone ? `Phone: ${selectedRequest.invoice_data.customer.phone}` : "No contact details"}
                        </p>
                      </div>
                      <div className="md:text-right">
                        <p className="text-[10px] font-black uppercase text-purple-500 tracking-wider">
                          Approval Status:
                        </p>
                        <div className="mt-1 md:flex justify-end">
                          {getStatusBadge(selectedRequest.status)}
                        </div>
                      </div>
                    </div>

                    {/* Items List */}
                    <div className="mt-6">
                      <p className="text-[10px] font-black uppercase text-purple-500 tracking-wider mb-3">
                        Product Selection List
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-slate-800 text-xs font-black text-slate-400 uppercase">
                              <th className="py-2.5">Product Item Name</th>
                              <th className="py-2.5 text-right">Quantity</th>
                              <th className="py-2.5 text-right">Unit Price</th>
                              <th className="py-2.5 text-right">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                            {(selectedRequest.invoice_data?.items || []).map((item, idx) => (
                              <tr key={idx} className="text-slate-700 dark:text-slate-300">
                                <td className="py-3 font-bold text-gray-900 dark:text-white">{item.name}</td>
                                <td className="py-3 text-right font-semibold">{parseFloat(item.qty)} {item.unit || 'units'}</td>
                                <td className="py-3 text-right font-semibold">₹{parseFloat(item.price).toFixed(2)}</td>
                                <td className="py-3 text-right font-black text-gray-900 dark:text-white">
                                  ₹{(item.qty * item.price).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Summary Totals */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-800 flex justify-end">
                      <div className="w-80 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                        <div className="flex justify-between font-medium">
                          <span>Subtotal Amount:</span>
                          <span className="font-extrabold text-gray-900 dark:text-white">
                            ₹{(selectedRequest.invoice_data?.subtotal || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between font-medium">
                          <span>CGST (9%):</span>
                          <span className="font-extrabold text-gray-900 dark:text-white">
                            ₹{(selectedRequest.invoice_data?.cgst || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between font-medium">
                          <span>SGST (9%):</span>
                          <span className="font-extrabold text-gray-900 dark:text-white">
                            ₹{(selectedRequest.invoice_data?.sgst || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-base font-black border-t border-gray-200 dark:border-slate-800 pt-3 text-purple-600 dark:text-purple-400">
                          <span>GRAND TOTAL:</span>
                          <span>₹{parseFloat(selectedRequest.total_amount).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons footer inside preview modal */}
                <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3 bg-slate-50 dark:bg-slate-950 p-4 -mx-6 -mb-6">
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedRequest(null);
                    }}
                    className="px-5 py-2.5 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-white rounded-xl text-xs font-black uppercase tracking-wider transition"
                  >
                    Close Preview
                  </button>
                  {selectedRequest.status === "pending" && (
                    <>
                      <button
                        onClick={() => handleReject(selectedRequest.id)}
                        disabled={actionLoading}
                        className="px-5 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-xs font-black uppercase tracking-wider transition flex items-center gap-2 shadow-md hover:shadow-rose-500/10"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject Request
                      </button>
                      <button
                        onClick={() => handleApprove(selectedRequest.id)}
                        disabled={actionLoading}
                        className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-95 text-white rounded-xl text-xs font-black uppercase tracking-wider transition flex items-center gap-2 shadow-md hover:shadow-emerald-500/10"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve Request
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
  );
}
